/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  makeStyles, Container, Grid, Box, ButtonBase,
} from '@material-ui/core';
import ERPagination from 'components/ERPagination';
import HeadingMD from 'common/ui_components/headings/headingMD';
import MotorForRentCard from 'views/profiles/common/components/MotorForRentCard';
import { withTranslation } from 'config/i18n';
import axios, { API } from 'config/axios';
import { useLayoutLoadingSpinner } from 'components/shared/Layout';
import useHandleResponse from 'utils/useHandleResponse';
import addressFormat from 'common/utils/address/address_format';
import scrollTo from 'utils/ScrollTo';

const useStyles = makeStyles((theme) => ({
  titleHolder: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    alignItems: 'center',

    [theme.breakpoints.up('md')]: {
      marginBottom: '0',
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  pricingTabs: {
    display: 'flex',

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      justifyContent: 'center',
      width: '100%',
    },
  },
  tab: {
    width: theme.spacing(10.5),
    padding: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.grey['300'],
    backgroundColor: theme.palette.grey['50'],
    textAlign: 'center',

    '&:first-child': {
      borderTopLeftRadius: 30,
      borderBottomLeftRadius: 30,
    },

    '&:last-child': {
      borderTopRightRadius: 30,
      borderBottomRightRadius: 30,
    },

    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.black,
      fontWeight: '700',
    },
  },
}));

const MotorsForRent = (props) => {
  const {
    t, vehiclesArr, userID, hourlyVehiclesCount, dailyVehiclesCount,
  } = props;
  const classes = useStyles();

  const [page, setPage] = useState(1);

  const { handleError } = useHandleResponse();
  const [loading, setLoading] = useLayoutLoadingSpinner();

  const [activePricing, setActivePricing] = useState('daily'); // ["daily" , "hourly"]
  const handlePricingChange = (pricing) => {
    setActivePricing(pricing);
  };
  const [dailyVehicles, setDailyVehicles] = useState([]);
  const [hourlyVehicles, setHourlyVehicles] = useState([]);
  const populateVehicles = (vehicles) => {
    // for backward comptability we will add vehicles with no price option to daily only
    const filteredDailyVehicles = vehicles.filter((vehicle) => vehicle.pricing_option !== 'hourly');
    const filteredHourlyVehicles = vehicles.filter((vehicle) => vehicle.pricing_option !== 'daily');

    setDailyVehicles(filteredDailyVehicles);
    setHourlyVehicles(filteredHourlyVehicles);
  };
  const getVehicles = (currentPage, perPage, pricing) => {
    setLoading(true);
    const vehiclePricingType = pricing ? { vehicle_pricing_type: pricing } : {};
    axios
      .get(API.profile.ownersVehicles.replace('{owner_id}', userID), {
        params: {
          page_number: currentPage,
          page_size: perPage,
          ...vehiclePricingType,
        },
      })
      .then((res) => {
        populateVehicles(res.data.vehicles);
      })
      .catch((error) => {
        handleError(error);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleVehiclesPagination = (value, pricing) => {
    setPage(value);
    getVehicles(value, 6, pricing || activePricing);
    !pricing && scrollTo('motors_for_rent', -50);
  };
  useEffect(() => {
    if (hourlyVehiclesCount > 0) setActivePricing('hourly');
    if (dailyVehiclesCount > 0) setActivePricing('daily');
    populateVehicles(vehiclesArr);
  }, []);
  const renderPagination = (vehicleTypeCount) => (
    <>
      {vehicleTypeCount > 6 && (
        <Box mt={5}>
          <ERPagination
            data={{
              pages_count: Math.ceil(vehicleTypeCount / 6),
              active_page: page,
            }}
            handleChange={handleVehiclesPagination}
          />
        </Box>
      )}
    </>
  );
  const renderVehicleList = (vehicleListType, vehicleTypeCount) => (
    <>
      <div className={classes.motors}>
        <Grid container spacing={1}>
          {vehicleListType.map((motor) => (
            <MotorForRentCard
              name={`${motor.year} ${motor.make?.name} ${
                motor.model?.name
              }`}
              className={classes.animated}
              location={addressFormat({
                country: motor.country,
                geo: motor.geography?.name,
                region: motor.region,
              })}
              key={motor.id}
              rating={motor.rating}
              tripsCount={motor.trips_count}
              bikePageurl={`/${motor.make.slug}/${motor.model.slug}/${motor.geography.slug}/${motor.id}`}
              price={
                activePricing === 'hourly'
                  ? { original_price: motor.hourly_price, discounts: [] }
                  : motor.price
              }
              image={motor.image_url}
              hourly={activePricing === 'hourly'}
            />
          ))}
        </Grid>
      </div>
      {renderPagination(vehicleTypeCount)}
    </>
  );

  return (
    <>
      <Container>
        <div className={classes.titleHolder}>
          <div className={classes.title} id="motors_for_rent">
            <HeadingMD title={t('fe_es_profiles:motors_for_rent')} />
          </div>

          {hourlyVehiclesCount > 0 && dailyVehiclesCount > 0 && (
          <Box className={classes.pricingTabs}>
            <ButtonBase
              className={`${classes.tab} ${activePricing == 'hourly' && 'active'}`}
              onClick={() => {
                if (activePricing !== 'hourly') {
                  handlePricingChange('hourly');
                  handleVehiclesPagination(1, 'hourly');
                }
              }}
            >
              {t('fe_es_profiles:hourly_pricing')}
            </ButtonBase>

            <ButtonBase
              className={`${classes.tab} ${activePricing == 'daily' && 'active'}`}
              onClick={() => {
                if (activePricing !== 'daily') {
                  handlePricingChange('daily');
                  handleVehiclesPagination(1, 'daily');
                }
              }}
            >
              {t('fe_es_profiles:daily_pricing')}
            </ButtonBase>
          </Box>
          )}
        </div>
        {!loading && (activePricing === 'hourly'
          ? renderVehicleList(hourlyVehicles, hourlyVehiclesCount)
          : renderVehicleList(dailyVehicles, dailyVehiclesCount))}

      </Container>
    </>
  );
};

export default withTranslation('fe_es_profiles')(MotorsForRent);
